import AWS from 'aws-sdk';
import https from 'https';

// performance improvement as described here:
// https://theburningmonk.com/2019/02/lambda-optimization-tip-enable-http-keep-alive/
const sslAgent = new https.Agent({
  keepAlive: true,
  maxSockets: 50,
  rejectUnauthorized: true
});

// high retry count because we really want these calls to happen vs throwing errors to the client
// (we explicitly prefer slower, more-likely desired response than faster response with failure)
AWS.config.update({ region: 'us-east-1', maxRetries: 20, httpOptions: { agent: sslAgent } });
const docClient = new AWS.DynamoDB.DocumentClient({ apiVersion: '2012-08-10' });

export function insert(TableName: string, Item: Record<string, any>) {
  const insertItem = {
    TableName,
    Item
  };

  return docClient.put(insertItem).promise();
} // insert

type BatchWriteRequest = {
  requestType: 'PutRequest' | 'DeleteRequest';
  keyName: string;
  keyValue: any;
  attributes: Record<string, any> | undefined;
  condition?: string;
};

type BatchWriteParams = {
  tableName: string;
  requests: BatchWriteRequest[];
};

type DDBPutRequest = {
  Item: Record<string, any>;
  ConditionExpression?: string;
};

type DDBBatchPutRequest = {
  PutRequest: DDBPutRequest;
};

// DDB batchWriteItem has a maximum request size of 16MB and up to 25 item operations
// can either do PutRequest or DeleteRequest
// can do multiple tables
export function batchWrite(batchWriteRequests: BatchWriteParams[]) {
  const batchRequestObject = {
    RequestItems: {}
  };

  for (const tableRequest of batchWriteRequests) {
    const tableRequestArray = [];

    for (const requestItem of tableRequest.requests) {
      if (requestItem.requestType === 'PutRequest') {
        const itemAttributeObject = requestItem.attributes || {};
        if (requestItem.keyName && requestItem.keyValue) {
          (<any>itemAttributeObject)[requestItem.keyName] = requestItem.keyValue;
        }

        const request: DDBBatchPutRequest = {
          PutRequest: {
            Item: itemAttributeObject
          }
        };
        if (requestItem.condition) {
          request.PutRequest.ConditionExpression = requestItem.condition;
        }
        tableRequestArray.push(request);
      } else {
        // requestItem.requestType === 'DeleteRequest'
        tableRequestArray.push({
          DeleteRequest: {
            Key: { [requestItem.keyName]: requestItem.keyValue }
          }
        });
      }
    }

    batchRequestObject.RequestItems = {
      ...batchRequestObject.RequestItems,
      [tableRequest.tableName]: tableRequestArray
    };
  }

  return docClient.batchWrite(batchRequestObject).promise();
}

export function get<T>(TableName: string, Key: AWS.DynamoDB.DocumentClient.Key): Promise<T> {
  const getItem = { TableName, Key };

  return new Promise((resolve, reject) => {
    docClient
      .get(getItem)
      .promise()
      .then((data) => resolve(data.Item as T))
      .catch((err) => {
        console.error(err, err.stack);
        reject(err);
      });
  });
}

type BatchGetItemParams = {
  TableName: string;
  KeyArray: AWS.DynamoDB.DocumentClient.Key[];
  ExpressionAttributeNames?: string;
};

// DDB batchGet has a maximum return of 16 MB and up to 100 items
export function batchGetOneTable({ TableName, KeyArray, ExpressionAttributeNames }: BatchGetItemParams) {
  const batchRequestObject = {
    RequestItems: {
      [TableName]: {
        Keys: KeyArray,
        ProjectionExpression: ExpressionAttributeNames
      }
    }
  };
  return new Promise((resolve, reject) => {
    docClient
      .batchGet(batchRequestObject)
      .promise()
      .then((data) => {
        // returning the whole data object so the recipient can see if there are any unprocessed keys
        resolve(data);
      })
      .catch((err) => {
        console.error(err, err.stack);
        reject(err);
      });
  });
}

export function updateWithConditionExpressionAndThenSave(
  TableName: string,
  Item: Record<string, any>,
  Key: AWS.DynamoDB.DocumentClient.Key,
  ConditionExpression: AWS.DynamoDB.DocumentClient.ConditionExpression,
  ExpressionAttributeValues: AWS.DynamoDB.DocumentClient.ExpressionAttributeValueMap,
  SaveTableName: string,
  SaveTableItem: Record<string, any>
) {
  // we will update this with dynamo transactions when the docClient supports them,
  // which should be by launch (this comment left 1/21/2019)

  // for now, three separate calls:
  // 1. delete old policy record
  // 2. put new (revised) policy record
  // 3. put old policy record into policyaudit table

  // we update by deleting if version number matches and then inserting
  // note that if the delete fails, the insert does not happen
  console.log(
    `update condition: ${TableName}, ${JSON.stringify(Key)}, ${ConditionExpression}, ${JSON.stringify(
      ExpressionAttributeValues
    )}`
  );
  return docClient
    .delete({
      TableName,
      Key,
      ConditionExpression,
      ExpressionAttributeValues
    })
    .promise()
    .then(() => {
      console.log('in second phase');
      return docClient
        .put({ TableName, Item })
        .promise()
        .then(() => {
          return docClient.put({ TableName: SaveTableName, Item: SaveTableItem }).promise();
        });
    })
    .catch((err) => {
      console.log(`error in delete: ${err} (${JSON.stringify(err)})`);
    });
} // updateWithVersionCheck

type UpdateParams = {
  TableName: string;
  Key: AWS.DynamoDB.DocumentClient.Key;
  UpdateExpression: AWS.DynamoDB.DocumentClient.UpdateExpression;
  ExpressionAttributeValues: AWS.DynamoDB.DocumentClient.ExpressionAttributeValueMap;
  ExpressionAttributeNames?: AWS.DynamoDB.DocumentClient.ExpressionAttributeNameMap;
  ConditionExpression?: AWS.DynamoDB.DocumentClient.ConditionExpression;
};

export function update({
  TableName,
  Key,
  UpdateExpression,
  ExpressionAttributeValues,
  ExpressionAttributeNames,
  ConditionExpression
}: UpdateParams) {
  return docClient
    .update({
      TableName,
      Key,
      UpdateExpression,
      ExpressionAttributeValues,
      ExpressionAttributeNames,
      ConditionExpression
    })
    .promise();
}

/**
 * Query a DynamoDB table by partition key.
 */
export function query(TableName: string, partitionKeyName: string, partitionKeyValue: string): Promise<any> {
  const searchParams = {
    TableName,
    KeyConditionExpression: `${partitionKeyName} = :partitionkeyvalue`,
    ExpressionAttributeValues: { ':partitionkeyvalue': partitionKeyValue }
  };

  return new Promise((resolve, reject) => {
    docClient
      .query(searchParams)
      .promise()
      .then((data) => resolve(data.Items))
      .catch((err) => {
        console.error(err, err.stack);
        reject(err);
      });
  });
}

type DeleteItemParams = {
  TableName: string;
  Key: AWS.DynamoDB.DocumentClient.Key;
};

export function deleteItem({ TableName, Key }: DeleteItemParams) {
  return docClient.delete({ TableName, Key }).promise();
}
